import { useMemo, useState } from "react";

import { ArrowPathIcon } from "@heroicons/react/24/outline";

import useListingOptimisation from "../../data/useListingOptimisation";
import { IPart } from "../../models/Part";
import { ListingOptimisationStatus } from "../../models/listingOptimisation/IListingOptimisation";
import { summariseRelistingRules } from "../../utils/relistingRule";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import BulkRelistResultModal, { BulkRelistResultData } from "../ebayListings/BulkRelistResultModal";
import RelistConfirmModal from "./RelistConfirmModal";

interface IOptimisationPageHeaderProps {
  /** The rows on the current page - the auto-relist badge reads their category rules. */
  rows: IPart[];
}

/**
 * Title, the auto-relist rule badge for the categories on screen, and the two actions that act on
 * everything staged: Discard staged and Send to Relist (with the staged count). Both confirm.
 */
export default function OptimisationPageHeader({ rows }: IOptimisationPageHeaderProps) {
  const { addNotification } = useNotification();
  const { pendingOptimisations, discardChanges, sendToRelist } = useListingOptimisation("OptimisationPageHeader");

  const [discardOpen, setDiscardOpen] = useState(false);
  const [relistOpen, setRelistOpen] = useState(false);
  const [relistResult, setRelistResult] = useState<BulkRelistResultData | null>(null);
  const [relistResultOpen, setRelistResultOpen] = useState(false);

  const stagedIds = useMemo(
    () =>
      (pendingOptimisations.data?.data ?? [])
        .filter((optimisation) => optimisation.status === ListingOptimisationStatus.Pending)
        .map((optimisation) => optimisation.partDataId),
    [pendingOptimisations.data]
  );

  const ruleSummary = useMemo(() => summariseRelistingRules(rows.map((row) => row.relistingRule)), [rows]);

  const handleDiscard = () => {
    setDiscardOpen(false);
    discardChanges.mutate(stagedIds, {
      onSuccess: () => addNotification({ primaryText: "Staged changes discarded", variant: "success" }),
      onError: () => addNotification({ primaryText: "Failed to discard staged changes", variant: "error" }),
    });
  };

  const handleRelist = (appendSku: boolean) => {
    setRelistOpen(false);
    sendToRelist.mutate(
      { partDataIds: stagedIds, appendSku },
      {
        onSuccess: (response) => {
          setRelistResult(response.data);
          setRelistResultOpen(true);
        },
        onError: () => addNotification({ primaryText: "Failed to send listings to be relisted", variant: "error" }),
      }
    );
  };

  const stagedCount = stagedIds.length;

  return (
    <>
      <ConfirmModal
        open={discardOpen}
        setOpen={setDiscardOpen}
        title={"Discard all staged changes?"}
        message={`This removes the staged changes from ${stagedCount} listing${stagedCount === 1 ? "" : "s"}.\nThe live eBay listings are unaffected.`}
        confirmButtonText={"Discard"}
        onConfirm={handleDiscard}
        isLoading={discardChanges.isLoading}
      />

      <RelistConfirmModal
        open={relistOpen}
        setOpen={setRelistOpen}
        count={stagedCount}
        stagedCount={stagedCount}
        onConfirm={handleRelist}
        isLoading={sendToRelist.isLoading}
      />

      <BulkRelistResultModal open={relistResultOpen} setOpen={setRelistResultOpen} result={relistResult} />

      <header className="flex flex-wrap items-center justify-between gap-4 px-1 pt-1">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900">eBay Optimisation</h1>
          <p className="mt-0.5 text-sm text-gray-600">
            Listings that are not selling, oldest first. Review each one, save what you change, then send them to be relisted.
            <span
              className="ml-2 inline-flex items-center gap-1 text-xs text-gray-500"
              title={
                ruleSummary
                  ? "The auto-relist rule on the categories shown. Set per category under Admin › Relisting rules."
                  : "None of the categories on this page has an auto-relist rule."
              }
            >
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {ruleSummary?.text ?? "No auto-relist rule on these categories"}
            </span>
          </p>
        </div>

      </header>

      {/* The relist bar: only while something is waiting, and it stays put as the queue scrolls under the top bar */}
      {stagedCount > 0 && (
        <div
          role="status"
          className="sticky top-16 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5 shadow-sm"
        >
          <ArrowPathIcon className="h-5 w-5 shrink-0 text-sky-700" aria-hidden="true" />
          <span className="min-w-0 flex-1 text-sm text-sky-900">
            <strong className="font-semibold">{stagedCount.toLocaleString("en-GB")}</strong>
            {stagedCount === 1 ? " listing has" : " listings have"} saved changes waiting to be relisted. Nothing reaches eBay until you send them.
          </span>
          <button
            type="button"
            onClick={() => setDiscardOpen(true)}
            disabled={discardChanges.isLoading}
            className="inline-flex h-9 items-center rounded-md border border-sky-300 bg-white px-3 text-sm font-medium text-sky-900 shadow-sm transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Discard all
          </button>
          <button
            type="button"
            onClick={() => setRelistOpen(true)}
            disabled={sendToRelist.isLoading}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-green-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sendToRelist.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Send {stagedCount.toLocaleString("en-GB")} to Relist
          </button>
        </div>
      )}
    </>
  );
}
